import React, { useEffect, useState } from 'react';
import { getViewportFitDebug } from '../hooks/useSoftKeyboardViewportFix';

/**
 * 소프트 키보드 뷰포트 진단 오버레이 (임시 진단 도구 — 제품 UI 아님).
 *
 * Windows 개발 PC에서는 iOS Web Inspector를 쓸 수 없어(맥 전용), 키보드가 뜰 때의
 * 뷰포트 수치를 실기기 화면에 직접 띄워 확인한다. useSoftKeyboardViewportFix가
 * 판정에 쓰는 값(clientHeight·vv.height·pageTop)과 #root 적용 상태를 그대로 보여준다.
 * 키보드가 아닐 때의 높이 맞춤(fit clamp — body 100dvh가 fixed 기준 상자보다 길 때)과
 * 하단 탭바의 아래 끝 좌표(navB)도 함께 보여준다.
 *
 * 켜는 법:
 * - Safari: URL에 ?kbdebug 를 붙여 접속
 * - PWA(주소창 없음): 설정 탭 '앱 정보'를 3초 안에 7번 탭 (MobileSettingsScreen)
 */

let enabled = new URLSearchParams(window.location.search).has('kbdebug');
let listeners: Array<(on: boolean) => void> = [];

export function toggleKbDebug(): void {
  enabled = !enabled;
  listeners.forEach((l) => l(enabled));
}

export const KeyboardViewportDebug: React.FC = () => {
  const [on, setOn] = useState(enabled);
  const [, setTick] = useState(0);

  useEffect(() => {
    const l = (v: boolean) => setOn(v);
    listeners.push(l);
    return () => { listeners = listeners.filter((x) => x !== l); };
  }, []);

  useEffect(() => {
    if (!on) return;
    const vv = window.visualViewport;
    const tick = () => setTick((n) => n + 1);
    vv?.addEventListener('resize', tick);
    vv?.addEventListener('scroll', tick);
    window.addEventListener('focusin', tick);
    window.addEventListener('focusout', tick);
    // 이벤트가 아예 오지 않는 케이스(iOS 구버전 PWA의 visualViewport 침묵)도 수치는 갱신돼야 한다
    const iv = window.setInterval(tick, 500);
    return () => {
      vv?.removeEventListener('resize', tick);
      vv?.removeEventListener('scroll', tick);
      window.removeEventListener('focusin', tick);
      window.removeEventListener('focusout', tick);
      clearInterval(iv);
    };
  }, [on]);

  if (!on) return null;
  const vv = window.visualViewport;
  const root = document.getElementById('root');
  const fit = getViewportFitDebug();
  // 하단 탭바(MobileTabBar)의 아래 끝 — fixH보다 크면 화면 밖으로 밀려난 것이다(2026-11-05 안드로이드 PWA)
  const tabBar = document.querySelector('nav[aria-label="주요 화면"]');
  const navBottom = tabBar ? Math.round(tabBar.getBoundingClientRect().bottom) : '-';
  const standalone =
    typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches;
  const lines = [
    `inH ${window.innerHeight}  clH ${document.documentElement.clientHeight}`,
    `vvH ${vv ? Math.round(vv.height) : 'X'}  pgT ${vv ? Math.round(vv.pageTop) : 'X'}  sc ${vv ? vv.scale.toFixed(2) : 'X'}`,
    // pageTop = offsetTop + scrollY — iOS가 키보드 패닝을 둘 중 어느 쪽으로 표현하는지 판별용
    // (ContextMenu가 헤더 쪽에 뜨던 사고의 좌표계 진단. pgT만으로는 이 배분이 안 보인다)
    `offT ${vv ? Math.round(vv.offsetTop) : 'X'}  scrY ${Math.round(window.scrollY)}`,
    `root ${root?.style.height || '-'} ${root?.style.transform || '-'}`,
    // 앱 컬럼 높이 판정(useSoftKeyboardViewportFix) — bodyH는 CSS 100dvh 결과, fixH는 fixed 기준 상자
    `fit ${fit.mode}  bodyH ${Math.round(fit.bodyHeight)}  fixH ${Math.round(fit.fixedHeight)}`,
    `navB ${navBottom}  dm ${standalone ? 'standalone' : 'browser'}`,
  ];
  return (
    <div
      // 진단용 임시 오버레이 — 의도적으로 디자인 토큰 미적용. #root 안의 fixed라
      // 키보드 보정(transform) 중에도 보이는 영역 좌상단에 붙는다.
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top) + 4px)',
        left: 4,
        zIndex: 99999,
        pointerEvents: 'none',
        background: 'rgba(0,0,0,0.72)',
        color: '#4ade80',
        font: '11px/1.5 ui-monospace, Menlo, monospace',
        padding: '4px 7px',
        borderRadius: 6,
        whiteSpace: 'pre',
      }}
    >
      {lines.join('\n')}
    </div>
  );
};
